import {BehaviorSubject, Subject} from 'rxjs';
import {Directive, EventEmitter, inject, Input, OnDestroy, Output, ViewChild} from '@angular/core';
import {Table, TableLazyLoadEvent} from "primeng/table";
import {FilterMatchMode, FilterMetadata, LazyLoadEvent, PrimeNGConfig} from "primeng/api";
import {GLOBAL} from '../constants';
import {BaseListFilterWithSpecs, FilterModel, PagedData} from "../models/filter.model";
import {PropertyQuery} from "../system/properties/property.query";
import {distinctBy} from "../helpers/array-helpers";
import {AppConfig} from "../app.config";
import {FilteredComponent} from "./filtered-component.directive";

@Directive()
export class TableComponent extends FilteredComponent implements OnDestroy {
  public rowsPerPageOptions = [10, 25, 50, 100, 200];

  Classificators = GLOBAL.Classificators;
  protected isRestored: boolean = false;
  public hasFilters: boolean = false;
  allSpecs: { Key: string, Value: any[] }[] = [];
  reloadOnClose: boolean = false;
  tableDropdownClosed: EventEmitter<void> = new EventEmitter<void>();
  @ViewChild(Table, {static: true}) table: Table;

  tableFilters: {
    [s: string]: FilterMetadata | FilterMetadata[];
  } = {};
  @Input() search$: BehaviorSubject<string>;
  @Input() tableSortField?: string = 'Code';
  @Input() tableSortOrder: number = 1;

  @Input() filterChange: BaseListFilterWithSpecs;
  @Output() filterChanged = new EventEmitter<boolean>();
  primeNgConfig = inject(PrimeNGConfig);

  choiceMatchModeOptions = [
    {label: this.primeNgConfig.translation.equals, value: FilterMatchMode.EQUALS},
    {label: this.primeNgConfig.translation.notEquals, value: FilterMatchMode.NOT_EQUALS},
    {label: this.primeNgConfig.translation.contains, value: FilterMatchMode.CONTAINS},
    {label: this.primeNgConfig.translation.notContains, value: FilterMatchMode.NOT_CONTAINS},
    {label: this.primeNgConfig.getTranslation('exists') ?? 'Exists', value: 'exists'}
  ];
  dateMatchModeOptions = [
    {label: this.primeNgConfig.getTranslation('between') ?? 'Between', value: 'between'},
    {label: this.primeNgConfig.translation.after, value: FilterMatchMode.DATE_AFTER},
    {label: this.primeNgConfig.translation.before, value: FilterMatchMode.DATE_BEFORE},
    {label: this.primeNgConfig.translation.is, value: FilterMatchMode.DATE_IS},
    {label: this.primeNgConfig.translation.isNot, value: FilterMatchMode.DATE_IS_NOT}
  ];

  numericMatchModeOptions = [
    {label: this.primeNgConfig.translation.equals, value: FilterMatchMode.EQUALS},
    {label: this.primeNgConfig.translation.notEquals, value: FilterMatchMode.NOT_EQUALS},
    {label: this.primeNgConfig.translation.contains, value: FilterMatchMode.CONTAINS},
    {label: this.primeNgConfig.translation.notContains, value: FilterMatchMode.NOT_CONTAINS},
    {label: this.primeNgConfig.getTranslation('exists') ?? 'Exists', value: 'exists'},
    {label: this.primeNgConfig.translation.lt, value: FilterMatchMode.LESS_THAN},
    {label: this.primeNgConfig.translation.lte, value: FilterMatchMode.LESS_THAN_OR_EQUAL_TO},
    {label: this.primeNgConfig.translation.gt, value: FilterMatchMode.GREATER_THAN},
    {label: this.primeNgConfig.translation.gte, value: FilterMatchMode.GREATER_THAN_OR_EQUAL_TO}
  ];
  loadDebounceTime = 100;
  lazyLoadStream = new Subject<LazyLoadEvent>();
  appConfig = inject(AppConfig);

  ngOnDestroy(): void {
    this.componentDestroyed.next();
    this.componentDestroyed.unsubscribe();
  }

  onLazyLoad(event: TableLazyLoadEvent) {
    this.lazyLoadStream.next(event as LazyLoadEvent);
  }

  applyDefaultFilters() {
    this.tableFilters = this.table?.filters ?? {};
    for (let tableFiltersKey in this.tableFilters) {
      const isSpecFilter = tableFiltersKey.startsWith('Specs.');
      if (Array.isArray(this.tableFilters[tableFiltersKey])) {
        for (const tableFilterElement of this.tableFilters[tableFiltersKey] as FilterMetadata[]) {
          tableFilterElement.value = isSpecFilter ? '*' : null;
        }
      } else {
        (this.tableFilters[tableFiltersKey] as FilterMetadata).value = isSpecFilter ? '*' : null;
      }
      if (isSpecFilter) {
        delete this.tableFilters[tableFiltersKey];
      }
    }
    for (let key of Object.keys(this.defaultFilter)) {
      const filterMetadata = this.defaultFilter[key];
      if (Array.isArray(filterMetadata)) {
        this.tableFilters[key] = [...filterMetadata.map(k => Object.assign({}, k))] as FilterMetadata | FilterMetadata[];
      } else {
        this.tableFilters[key] = Object.assign({}, filterMetadata) as FilterMetadata | FilterMetadata[];
      }
    }

    this.tableSortOrder = this.sortOrder;
    this.tableSortField = this.sortField;
  }

  prepareLoad(filter: FilterModel<BaseListFilterWithSpecs>, event: LazyLoadEvent = null) {
    if (filter.Data?.SelectedSpecs) {
      filter.Data.SelectedSpecs = distinctBy([...filter.Data.SelectedSpecs], obj => obj);
    }

    if (event) {
      filter.setPage(event);
      if (event.filters) {
        filter.setFilters(event.filters);
      }
      filter.Data.ensureSelectedSpecs();
      filter.Global = event.filters?.global?.value ?? filter.Global;
      delete filter.Data['global'];
      filter.setSort(event);
      this.hasFilters = FilterModel.hasAny(event.filters);
      this.filterChanged.emit();
    }
  }

  updateAfterLoad(filter: FilterModel<BaseListFilterWithSpecs>, pagedData: PagedData<any>) {
    filter.Page = pagedData.Page;
    this.allSpecs = pagedData.DistinctSpecs;
    if (this.allSpecs?.length == 0) {
      this.allSpecs = [...filter.Data.SelectedSpecs.map(spec => {
        return {Key: spec, Value: []}
      })];
    }
  }

  toggleSpecSelection(property: PropertyQuery, filter: FilterModel<BaseListFilterWithSpecs>) {
    const key = 'Specs.' + property.Id;
    if (filter.Data.SelectedSpecs.some(spec => spec === property.Id)) {
      filter.Data.SelectedSpecs = [...filter.Data.SelectedSpecs.filter(spec => spec !== property.Id)];
      delete this.tableFilters[key];
      delete this.table?.filters[key];
    } else {
      filter.Data.SelectedSpecs = [...filter.Data.SelectedSpecs, property.Id];
      this.tableFilters[key] = property.TypeClv === GLOBAL.Classificators.PropertyType.DateTime
        ? [{matchMode: 'dateAfter', operator: 'and', value: '*'}]
        : [{matchMode: 'equals', operator: 'and', value: '*'}];
      this.table.filters[key] = this.tableFilters[key];
    }
    filter.setFilters(this.tableFilters);
    this.reloadOnClose = true;
  }

  toggleSpecFilterSelection(filter: FilterModel<BaseListFilterWithSpecs>) {
    this.table.filters["ShowOnlyRelevantSpecs"] = filter.Data.ShowOnlyRelevantSpecs;
    this.table.filters["IncludeInheritedSpecs"] = filter.Data.IncludeInheritedSpecs;
    this.table.filters["IncludeSpecConstraints"] = filter.Data.IncludeSpecConstraints;
    this.reloadOnClose = true;
  }

  getTableState(stateKey: string) {
    return JSON.parse((this.appConfig.primeNgStateStorage === "session" ? sessionStorage : localStorage).getItem(stateKey));
  }

  setTableState(stateKey: string, state: any) {
    (this.appConfig.primeNgStateStorage === "session" ? sessionStorage : localStorage).setItem(stateKey, JSON.stringify(state));
  }
}
